import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query"
import { type ReactNode, useState } from "react"

const FIVE_MINUTES = 5 * 60 * 1000
const THIRTY_SECONDS = 30 * 1000
const ONE_HOUR = 60 * 60 * 1000

const createQueryClient = () =>
  new QueryClient({
    queryCache: new QueryCache({
      onError: (_error, query) => {
        if (query.meta?.showErrorToast === false) {
          return
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (_error, _variables, _context, mutation) => {
        if (mutation.meta?.showErrorToast === false) {
          return
        }
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: FIVE_MINUTES,
        gcTime: ONE_HOUR,
        retry: (failureCount, error) => {
          if (error instanceof Error && "status" in error) {
            const status = (error as Error & { status: number }).status
            if (status === 401 || status === 403 || status === 404) {
              return false
            }
          }
          return failureCount < 2
        },
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        refetchInterval: false,
        networkMode: "online",
        throwOnError: false,
      },
      mutations: {
        retry: false,
        networkMode: "online",
        throwOnError: false,
        gcTime: THIRTY_SECONDS,
      },
    },
  })

export function QueryProvider({ children }: { children: ReactNode }) {
  const [queryClient] = useState(createQueryClient)

  return (
    <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
  )
}
